import { logger } from "@/utils/logger";
import { pushADFLead } from './adfLeadPush';
import { getDealerID } from './dealerDataUtils';
import { getCurrentSession } from './sessionManager';

// Extend Window interface for lead push history tracking
declare global {
  interface Window {
    leadPushHistory?: Array<{
      timestamp: string;
      contactData: any;
      success: boolean;
      source: string;
      error?: string;
    }>;
  }
}

interface ContactData {
  Name?: string;
  First_Name?: string;
  Last_Name?: string;
  Phone?: string;
  Email?: string;
}

interface InterestData {
  summary?: string;
  vehicle?: any;
  department?: string;
  dealerid?: string;
  utm_source?: string;
  utm_campaign?: string;
  intro?: string;
  closing?: string;
}

/**
 * Automatically push lead when AI response contains valid Contact data
 * Duplicate checking is handled by the caller (AutoLeadSubmission utility)
 */
export const processAutoLeadFromContact = async (
  contactData: ContactData,
  interestData?: InterestData
): Promise<{ success: boolean; leadId?: string }> => {
  logger.log('💰💰💰 === AUTO LEAD FROM CONTACT - PROCESSING ===');

  // VALIDATION: Check if contact data has Name AND (Phone OR Email)
  // Support both formats: single "Name" field or separate "First_Name"/"Last_Name"
  const fullName = contactData.Name || 
    (contactData.First_Name || contactData.Last_Name 
      ? `${contactData.First_Name || ''} ${contactData.Last_Name || ''}`.trim() 
      : '');
  
  const hasName = fullName && fullName.trim().length > 0;
  const hasPhone = contactData.Phone && contactData.Phone.trim().length > 0;
  const hasEmail = contactData.Email && contactData.Email.trim().length > 0;

  if (!hasName) {
    logger.log('💰💰💰 ❌ Missing Name - not pushing lead');
    return { success: false };
  }

  if (!hasPhone && !hasEmail) {
    logger.log('💰💰💰 ❌ Missing both Phone and Email - not pushing lead');
    return { success: false };
  }

  logger.log('💰💰💰 ✅ Contact data is valid (has Name + Phone/Email)');
  logger.log('💰💰💰 Full name used:', fullName);

  // PUSH ADF LEAD TO D2C API
  logger.log('💰💰💰 🚀 Calling pushADFLead function...');
  
  try {
    // Get dealer ID - CRITICAL: Must exist for lead push
    logger.log('💰💰💰 🔍 Getting dealer ID...');
    const dealerId = await getDealerID();
    logger.log('💰💰💰 📋 Dealer ID result:', dealerId);
    
    if (!dealerId) {
      console.error('💰💰💰 ❌ CRITICAL: No dealer ID found, cannot push lead!');
      console.error('💰💰💰 ❌ This is a FATAL error - lead will NOT be pushed to D2C API');
      return { success: false };
    }

    // Get session ID for tracking - try multiple sources
    logger.log('💰💰💰 🔍 Getting session ID...');
    let session = getCurrentSession();
    let sessionId = session?.sessionId;
    
    if (!sessionId) {
      // Try sessionStorage as fallback
      const storedSession = sessionStorage.getItem('chatbot_session');
      if (storedSession) {
        try {
          const parsed = JSON.parse(storedSession);
          sessionId = parsed.sessionId;
          logger.log('💰💰💰 📋 Session ID from sessionStorage fallback:', sessionId);
        } catch (e) {
          logger.warn('💰💰💰 ⚠️ Could not parse sessionStorage fallback');
        }
      }
    }
    
    sessionId = sessionId || `auto_${Date.now()}`;
    logger.log('💰💰💰 📋 Final Session ID:', sessionId);

    // Build comments with interest summary, intro, and closing
    const comments: string[] = [];
    
    // Add Reply_Message.Intro if available
    if (interestData?.intro) {
      comments.push(`Intro: ${interestData.intro}`);
    }
    
    if (interestData?.summary) {
      comments.push(`Customer interest: ${interestData.summary}`);
    }
    if (interestData?.department) {
      comments.push(`Department: ${interestData.department}`);
    }
    
    // Add Reply_Message.Closing if available
    if (interestData?.closing) {
      comments.push(`Closing: ${interestData.closing}`);
    }
    
    comments.push(`Auto-generated lead from chat conversation (Session: ${sessionId})`);

    // Transform vehicle data if available
    const vehicles = [];
    if (interestData?.vehicle) {
      const v = interestData.vehicle;
      logger.log('💰💰💰 ✅ Vehicle data found, transforming...');
      vehicles.push({
        interest: 'buy' as const,
        status: (v.status === 'used' || v.status === 'Used') ? 'Used' as const : 'New' as const,
        year: v.year ? String(v.year) : '',
        make: v.make || '',
        model: v.model || '',
        trim: v.version || '',
        vin: v.VIN || v.vin || '',
        stock: v.Stock || v.stock || '',
        price: ''
      });
    } else {
      logger.log('💰💰💰 ⚠️ No vehicle data in interestData');
    }

    logger.log('💰💰💰 📤 Pushing ADF lead with dealer ID:', dealerId);
    logger.log('💰💰💰 📤 Vehicles:', vehicles);

    // Push the ADF lead with correct parameter structure
    // Include interestData so AI summary can be extracted from interestData.message
    const result = await pushADFLead({
      prospect: {
        id: sessionId
      },
      customer: {
        name: fullName,
        phone: contactData.Phone || '',
        email: contactData.Email || ''
      },
      vehicles: vehicles.length > 0 ? vehicles : undefined,
      comments: comments.join('\n'),
      formType: 'Contact',
      interestData: interestData // Pass interestData for AI summary extraction
    });

    if (result.success) {
      logger.log('💰💰💰 ✅ ADF Lead successfully pushed! Lead ID:', result.leadId);
      
      // Track in global history for debugging
      if (typeof window !== 'undefined') {
        if (!window.leadPushHistory) {
          window.leadPushHistory = [];
        }
        window.leadPushHistory.push({
          timestamp: new Date().toISOString(),
          contactData: contactData,
          success: true,
          source: 'auto_lead_adf_push'
        });
      }

      return { success: true, leadId: result.leadId };
    } else {
      console.error('💰💰💰 ❌ pushADFLead returned false - lead was NOT pushed!');
      console.error('💰💰💰 ❌ Check adfLeadPush.ts logs for details');
      
      // Track failed attempts for debugging
      if (typeof window !== 'undefined') {
        if (!window.leadPushHistory) {
          window.leadPushHistory = [];
        }
        window.leadPushHistory.push({
          timestamp: new Date().toISOString(),
          contactData: contactData,
          success: false,
          error: 'pushADFLead returned false',
          source: 'auto_lead_adf_push'
        });
      }
      
      return { success: false };
    }
  } catch (error) {
    console.error('💰💰💰 💥 CRITICAL ERROR pushing ADF lead from AI Contact data:', error);
    console.error('💰💰💰 💥 Error details:', JSON.stringify(error, Object.getOwnPropertyNames(error)));
    
    // Track error for debugging
    if (typeof window !== 'undefined') {
      if (!window.leadPushHistory) {
        window.leadPushHistory = [];
      }
      window.leadPushHistory.push({
        timestamp: new Date().toISOString(),
        contactData: contactData,
        success: false,
        error: error instanceof Error ? error.message : String(error),
        source: 'auto_lead_adf_push'
      });
    }
    
    return { success: false };
  }
};
